import { mapValues } from 'lodash';

export const mapObject: <A extends object, B>(
  object: A,
  f: (value: A[keyof A], key: keyof A, object: A) => B,
) => { [K in keyof A]: B } = mapValues;

export function sliceObject<T extends {}>(
  object: T,
  start: number,
  end: number,
): Partial<T> {
  return Object.fromEntries(
    Object.entries(object).slice(start, end),
  ) as Partial<T>;
}

export type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

/** Merge objects and choose the 0th value for each key using `comparator`. */
export function mergeBestEntries<T>(
  objects: { [key: string]: T }[],
  comparator: (t1: T, t2: T) => number,
): { [key: string]: T } {
  const keys = new Set(objects.flatMap(object => Object.keys(object)));
  return Object.fromEntries(
    [...keys].map(key => [
      key,
      objects
        .filter(object => key in object)
        .map(object => object[key])
        .sort(comparator)[0],
    ]),
  );
}

/**
 * Recursively freeze an object and its inner objects. Useful for "empty"
 * objects meant to be used as a starting point e.g. `emptyPrimesuitePerson`,
 * so they can't get modified directly. Consumers should use lodash `cloneDeep`
 * first.
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/freeze
 */
export function deepFreeze<T>(object: T): T {
  if (object == null) return object;
  Object.freeze(object);
  Object.values(object).forEach(deepFreeze);
  return object;
}
